import { BaseEditor, Editor, Element as SlateElement, Location, Node, Path, Range, Text, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import { ReactEditor } from "slate-react";
import {
  PromptBlock,
  PromptHeadingNode,
  PromptInline,
  PromptLinkNode,
  PromptMark,
  PromptParagraphNode,
  PromptText,
  PromptVariableNode,
  UnsupportedMarkdown,
  looksLikeMarkdown,
  markdownToPromptBlocks,
} from "./markdown";

/**
 * The editor's behaviour, apart from its rendering: what a variable is, what the toolbar's buttons do, and
 * how a typed `{{name}}` becomes a chip.
 *
 * Kept out of the component because every one of these is a rule rather than a view, and the rules are where
 * the bugs were: the editor this replaced got them wrong three times over.
 */

export type PromptEditor = BaseEditor & ReactEditor & HistoryEditor;

declare module "slate" {
  interface CustomTypes {
    Editor: PromptEditor;
    Element: PromptBlock | PromptLinkNode | PromptVariableNode | { type: "list-item"; children: PromptInline[] };
    Text: PromptText;
  }
}

/**
 * A complete `{{name}}` sitting in a text node, which is what typing one produces.
 *
 * 🔴 NO DOTS, matching `variables.ts`. `{{a.b}}` is not a variable — the prompt engine throws on it — so it
 * must stay literal text and be reported as malformed rather than becoming a chip that merely looks
 * unfamiliar.
 */
const VARIABLE_IN_TEXT = /\{\{\s*(\w+)\s*\}\}/;

export const emptyPromptDocument = (): PromptParagraphNode[] => [{ type: "paragraph", children: [{ text: "" }] }];

/**
 * Variables and links are inline; a variable is also void.
 *
 * 🔴 VOID IS WHAT MAKES A CHIP ATOMIC. There is nothing editable inside it, so the cursor steps over it and a
 * deletion takes the whole thing — the behaviour AC-3 asks for, and the behaviour the previous editor needed
 * three attempts to approximate.
 *
 * `normalizeNode` is what turns a typed token into a chip. Slate runs it after every change and maps the
 * selection through the transform itself, which is the entire caret problem the previous editor had to solve
 * by hand.
 */
export function withPromptVariables<T extends Editor>(editor: T): T {
  const { isInline, isVoid, normalizeNode } = editor;

  editor.isInline = (element) => element.type === "variable" || element.type === "link" || isInline(element);
  editor.isVoid = (element) => element.type === "variable" || isVoid(element);

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (Text.isText(node)) {
      const match = VARIABLE_IN_TEXT.exec(node.text);

      if (match) {
        const at = {
          anchor: { path, offset: match.index },
          focus: { path, offset: match.index + match[0].length },
        };
        // The marks the token was typed under are kept, so `**{{x}}**` stays bold once it becomes a chip.
        const { text: unused, ...marks } = node;
        /**
         * Whether the operator is typing here: true when the caret is in the token that is about to become a
         * chip — they just closed the braces — and false when this runs over a document that arrived from
         * somewhere else, where moving the caret would be taking it off whatever they were doing.
         */
        const isTypingHere = !!editor.selection && Range.includes(at, editor.selection);

        Transforms.delete(editor, { at });
        Transforms.insertNodes(
          editor,
          { type: "variable", name: match[1], children: [{ text: "" }], ...marks },
          {
            at: at.anchor,
          }
        );

        if (isTypingHere) {
          /**
           * 🔴 IN THE TEXT AFTER THE CHIP, NEVER ON THE CHIP. `insertNodes({ select: true })` selects the
           * inserted node, and a void element's only DOM is a `contenteditable="false"` spacer: the caret
           * disappears and the next key press goes nowhere, because Slate will not insert text into a void.
           *
           * The point has to be computed rather than stepped to: both `Transforms.move` and `Editor.after`
           * land *inside* the void, which is the same broken position. Inserting at a text offset splits that
           * text, so the chip is the next sibling — unless the token started the node, in which case it took
           * its place.
           */
          const chipPath = at.anchor.offset === 0 ? at.anchor.path : Path.next(at.anchor.path);
          const afterChip = Path.next(chipPath);

          // Slate requires a text node after an inline; making that true now rather than next pass is what
          // gives the caret somewhere to be.
          if (!Editor.hasPath(editor, afterChip)) {
            Transforms.insertNodes(editor, { text: "" }, { at: afterChip });
          }
          Transforms.select(editor, Editor.start(editor, afterChip));
        }

        return;
      }
    }

    normalizeNode(entry);
  };

  return editor;
}

export const isMarkActive = (editor: Editor, mark: PromptMark) => !!Editor.marks(editor)?.[mark];

export function toggleMark(editor: Editor, mark: PromptMark) {
  if (isMarkActive(editor, mark)) {
    Editor.removeMark(editor, mark);
    return;
  }
  Editor.addMark(editor, mark, true);
}

const LIST_TYPES = ["bulleted-list", "numbered-list"] as const;

export type PromptListType = typeof LIST_TYPES[number];

/**
 * The first node matching a predicate, or nothing.
 *
 * `next().value` rather than destructuring the generator: this repo compiles to es5 without
 * `downlevelIteration`, so `const [first] = Editor.nodes(...)` is a type error. It also stops at the first
 * hit, which is all either caller wants.
 */
const firstMatch = (editor: Editor, match: (node: Node) => boolean, at?: Location) =>
  Editor.nodes(editor, { match, ...(at ? { at } : {}) }).next().value;

export function isListActive(editor: Editor, type: PromptListType) {
  return !!firstMatch(editor, (node) => SlateElement.isElement(node) && node.type === type);
}

/**
 * Turns the selected blocks into a list, or back into paragraphs.
 *
 * Both directions in one place because they are one decision: pressing the button a second time has to undo
 * it, and a list item outside a list is a document Slate will not keep.
 */
export function toggleList(editor: Editor, type: PromptListType) {
  const isActive = isListActive(editor, type);

  Transforms.unwrapNodes(editor, {
    match: (node) => SlateElement.isElement(node) && LIST_TYPES.includes(node.type as PromptListType),
    split: true,
  });
  Transforms.setNodes(editor, { type: isActive ? "paragraph" : "list-item" } as never);

  if (!isActive) {
    Transforms.wrapNodes(editor, { type, children: [] } as never);
  }
}

/**
 * Markdown pasted in becomes blocks (AICC2-328).
 *
 * 🔴 THE PROMPTS ARE WRITTEN SOMEWHERE ELSE. Operators paste them in from a doc or a ticket, and Slate's own
 * paste inserts `text/plain` as characters — so `## Role` arrived as four characters of prose and the prompt
 * had to be reformatted by hand. The same parser the screen loads with is the right one to run here: one
 * grammar, one set of tests, and pasted `{{variables}}` become chips for free.
 *
 * 🔴 WHAT IT CANNOT SHOW IS STILL NOT PARSED. A pasted table or code fence stays as the literal lines it was
 * — flattening it into paragraphs that look right and save wrong is the outcome this whole module exists to
 * avoid — and `onUnsupported` is how the screen says so while the operator is still looking at the paste.
 */
export function withMarkdownPaste<T extends Editor>(
  editor: T,
  onUnsupported?: (unsupported: UnsupportedMarkdown[]) => void
): T {
  const { insertData } = editor;

  editor.insertData = (data) => {
    const text = data.getData("text/plain");

    // Not a document: an image, a slate fragment, or a word dropped mid-sentence. All of those belong to the
    // behaviour Slate already has.
    if (!text || !looksLikeMarkdown(text)) {
      insertData(data);
      return;
    }

    const { blocks, unsupported } = markdownToPromptBlocks(text);

    if (unsupported.length) {
      onUnsupported?.(unsupported);
    }

    Transforms.insertFragment(editor, blocks as never);
  };

  return editor;
}

/**
 * The line-start markers this editor turns into blocks as they are typed, and only on the space that closes
 * them: `##` alone is text until it is followed by one.
 */
const HEADING_SHORTCUT = /^(#{1,6})$/;

const BULLET_SHORTCUT = /^[-*+]$/;

/** The number is not kept: a numbered list is renumbered from one on the way out, as the parser does. */
const NUMBERED_SHORTCUT = /^\d+[.)]$/;

/**
 * Typed markdown becomes blocks too (AICC2-328).
 *
 * 🔴 LINE START AND PARAGRAPHS ONLY. `- ` inside a list item means a nested list, which this editor cannot
 * show, and `#` in the middle of a sentence is a hash. Both stay as text, which is the same answer the parser
 * gives them.
 *
 * Inline emphasis is deliberately absent: turning `**x**` into bold as the closing asterisks are typed means
 * rescanning the line on every keystroke and reaching into the undo stack, and the paste path above already
 * covers the case this feature was asked for — prompts arriving from somewhere else.
 */
export function withMarkdownShortcuts<T extends Editor>(editor: T): T {
  const { insertText } = editor;

  editor.insertText = (text) => {
    const { selection } = editor;

    if (text !== " " || !selection || !Range.isCollapsed(selection)) {
      insertText(text);
      return;
    }

    const entry = Editor.above(editor, { match: (node) => Editor.isBlock(editor, node as never) });

    if (!entry || (entry[0] as PromptBlock).type !== "paragraph") {
      insertText(text);
      return;
    }

    const [, path] = entry;
    const beforeCaret = { anchor: Editor.start(editor, path), focus: selection.anchor };
    const marker = Editor.string(editor, beforeCaret);
    const heading = HEADING_SHORTCUT.exec(marker);
    const isList = BULLET_SHORTCUT.test(marker) || NUMBERED_SHORTCUT.test(marker);

    if (!heading && !isList) {
      insertText(text);
      return;
    }

    // The marker itself goes: it was the instruction, not the prompt's text.
    Transforms.delete(editor, { at: beforeCaret });

    if (heading) {
      Transforms.setNodes(editor, { type: "heading", level: heading[1].length } as never, { at: path });
      return;
    }

    toggleList(editor, BULLET_SHORTCUT.test(marker) ? "bulleted-list" : "numbered-list");
  };

  return editor;
}

/** 1–6, as markdown writes them, and what the toolbar's heading menu offers. */
export const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

/** The heading level the cursor is in, or nothing when it is in a paragraph or a list item. */
export function activeHeadingLevel(editor: Editor) {
  const entry = firstMatch(editor, (node) => SlateElement.isElement(node) && node.type === "heading");

  return entry ? ((entry[0] as PromptHeadingNode).level as number | undefined) : undefined;
}

/**
 * Sets the block to a heading of that level, or back to a paragraph when the level is left out.
 *
 * 🔴 THE LIST HAS TO BE UNWRAPPED FIRST. A `list-item` with `type: "heading"` written over it is a heading
 * inside a `<ul>` — a document the serialiser has no rule for and Slate will not keep. Turning the list off
 * on the way is what the toolbar's list button does in the other direction.
 */
export function setHeading(editor: Editor, level?: number) {
  Transforms.unwrapNodes(editor, {
    match: (node) => SlateElement.isElement(node) && LIST_TYPES.includes(node.type as PromptListType),
    split: true,
  });
  Transforms.setNodes(editor, (level ? { type: "heading", level } : { type: "paragraph" }) as never);
}

/** Inserts a chip at the cursor — what the dictionary's [+] button calls. */
export function insertVariable(editor: Editor, name: string) {
  Transforms.insertNodes(editor, { type: "variable", name, children: [{ text: "" }] });
  // After it rather than inside it: a void element has no inside to be in.
  Transforms.move(editor);
}

/** Wraps the selection in a link, using the selected text as the label when there is any. */
export function insertLink(editor: Editor, url: string) {
  if (!url) {
    return;
  }

  const { selection } = editor;
  const isCollapsed = !selection || Range.isCollapsed(selection);
  const link: PromptLinkNode = { type: "link", url, children: [{ text: isCollapsed ? url : "" }] };

  if (isCollapsed) {
    Transforms.insertNodes(editor, link as never);
    return;
  }

  Transforms.wrapNodes(editor, link as never, { split: true });
  Transforms.collapse(editor, { edge: "end" });
}

/**
 * Clears the inline formatting the toolbar can apply, which is what the "Tx" button means: marks off, links
 * unwrapped. Block structure — headings, lists — is left alone, since those have buttons of their own.
 */
export function clearFormatting(editor: Editor) {
  (["bold", "italic", "underline"] as PromptMark[]).forEach((mark) => Editor.removeMark(editor, mark));
  Transforms.unwrapNodes(editor, {
    match: (node) => SlateElement.isElement(node) && node.type === "link",
    split: true,
  });
}

/**
 * Replaces the whole document, for when the value arrives from outside — a prompt being switched, or Reset to
 * Default landing.
 *
 * Assigning `children` directly is the recipe Slate's own discussions land on: the transform-based routes
 * either leave the selection pointing into removed nodes or fire a change per node.
 */
export function resetPromptDocument(editor: Editor, blocks: PromptBlock[]) {
  editor.children = (blocks.length ? blocks : emptyPromptDocument()) as never;
  editor.selection = null;
  editor.onChange();
}

/** The document as blocks, for serialising back to markdown. */
export const promptDocumentBlocks = (editor: Editor) => (editor.children as unknown) as PromptBlock[];

/** Whether the document holds nothing but empty text — an empty prompt, which cannot be saved. */
export const isPromptDocumentEmpty = (editor: Editor) => !Node.string(editor).trim() && !hasVariable(editor);

const hasVariable = (editor: Editor) =>
  !!firstMatch(editor, (node) => SlateElement.isElement(node) && node.type === "variable", []);
